import { Button, Col, Form, Row, Tag } from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { FilterConfig, FilterValues } from 'types';
import React, { useCallback, useContext, useEffect, useState } from 'react';

import { AuthContext } from 'components/Auth/Auth';
import ConfirmModal from 'components/theme_components/ThirdParty/ConfirmModal';
import DataGrid from 'components/theme_components/DataGrid';
import DisplayFramework from 'DisplayFramework';
import ExportXLS from 'components/theme_components/ExportXLS';
import { FormInstance } from 'antd/lib/form';
import GetLabel from 'DisplayFramework/components/GetLabel';
import HeroTitle from 'components/theme_components/HeroTitle';
import { findFinModificationByAnyRefOrStatus } from 'services/fincontract';
import moment from 'moment';
import { toCurrency } from 'utils/utils';
import { useQuery } from 'react-query';
import TaskSButtonSFS from 'pages/workflow/workflowSFS/TasksButtonSFS';
import ButtonOperation from 'components/ButtonOperation';

export type DemandSubTabProps = {
  form: FormInstance;
  color?: string;
  tpid?: number;
  itemsCompanyHierarchyTree: any[];
  isActive?: boolean; // New prop to track if tab is active
  onTabActivated?: () => void; // Callback when tab becomes active
};

const DemandSubTab = (props: DemandSubTabProps) => {
  const {
    form,
    color,
    tpid,
    itemsCompanyHierarchyTree,
    isActive,
    onTabActivated,
  } = props;
  const [filterForm] = Form.useForm();
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [searchParams, setSearchParams] = useState({});
  const { isInspect } = useContext(AuthContext);
  const [modalVisibleConfirm, setmodalVisibleConfirmVisible] = useState(false);

  const {
    data: modList,
    refetch,
    isFetching,
  } = useQuery(
    ['findFinModificationByAnyRefOrStatus', searchParams],
    () =>
      findFinModificationByAnyRefOrStatus({
        ...searchParams,
        ctrid: form.getFieldValue('id') ?? 0,
        tpid: tpid,
        ctreference: '',
        modstatus: '',
        modtype: 'SIMUL',
      }).then((res) => {
        return res.list || [];
      }),
    { refetchOnMount: false, enabled: true }
  );

  // Default (local) filters and columns
  const filterConfig: FilterConfig[] = [
    {
      key: 'SFSREQTYPE',
      label: 'modevent',
      type: 'select',
    },
    {
      key: 'vin2',
      label: 'vin2',
      type: 'text',
    },
    {
      key: 'reviewdate',
      label: 'reviewdate',
      type: 'date',
    },
    {
      key: 'reviewdatetowish',
      label: 'reviewdatetowish',
      type: 'date',
    },
  ];

  const columns = [
    {
      dataIndex: 'creationdate',
      key: 'creationdate',
      render: (date: string) => (date ? moment(date).format('DD/MM/YYYY') : ''),
    },
    {
      dataIndex: 'ctreference',
      key: 'ctreference',
    },
    {
      dataIndex: 'eventlabel',
      key: 'eventlabel',
    },
    {
      dataIndex: 'vin2',
      key: 'vin2',
    },
    {
      dataIndex: 'reviewdate',
      key: 'reviewdate',
      render: (date: string) => (date ? moment(date).format('DD/MM/YYYY') : ''),
    },
    {
      dataIndex: 'modstatus',
      key: 'modstatus',
      render: (modstatus: string, record: any) => (
        <Tag
          color={
            modstatus === 'VALID'
              ? '#D6E9E6'
              : modstatus === 'REFUSE'
                ? 'orange'
                : '#d9d9d9'
          }
          className="tag-status tag-status-default ml-2 p-2"
          bordered={false}
          icon={
            modstatus === 'VALID' ? (
              <CheckOutlined color="#0A7C53" />
            ) : modstatus === 'REFUSE' ? (
              <CloseOutlined color="#DD7248" />
            ) : null
          }
        >
          {record.modstatuslabel || modstatus}
        </Tag>
      ),
    },
    {
      dataIndex: 'reviewdatetowish',
      key: 'reviewdatetowish',
      render: (date: string) => (date ? moment(date).format('DD/MM/YYYY') : ''),
    },
    {
      dataIndex: 'finamount',
      key: 'finamount',
      render: (val: number) => toCurrency(val),
    },
    {
      dataIndex: 'action',
      key: 'action',
      width: 200,
      render: (val: number, record: any) => {
        if (record.modstatus === 'INIT' && record.modevent === "PREFINREIM") {
          return (
            <TaskSButtonSFS
              clientName=""
              entityId={record?.ctrid}
              entityReference=""
              entityName="FINCONTRAT"
              currcode=""
              funcode="evSfsAcceptpay"
              // refetch={() => refetch()}
              width={550}
              title="evSfsAcceptpay"
              refetch={() => {
                refetch();
              }}
              dataInput={record}
            >
              <ButtonOperation
                type="primary"
                comcode='accpay'
              />
            </TaskSButtonSFS>
          )
        } else if (record.modstatus === 'INIT' && record.modevent === "PREFINRENE") {

          return (<TaskSButtonSFS
            clientName=""
            entityId={record?.ctrid}
            entityReference=""
            entityName="FINCONTRAT"
            currcode=""
            funcode="evAcceptConversion"
            // refetch={() => refetch()}
            width={550}
            title="evAcceptConversion"
            refetch={() => {
              // refetch();
            }}
            dataInput={record}
          >
            <ButtonOperation
              type="primary"
              comcode='accconv'
            />
          </TaskSButtonSFS>)
        }

      },
    },
  ];

  const colsXLS = [
    'creationdate',
    'requesttype',
    'eventlabel',
    'vin2',
    'reviewdate',
    'modstatuslabel',
    'reviewdatetowish',
    'finamount',
  ];
  // Define columnLabels for ExportXLS
  const columnLabels: Record<string, string> = {
    creationdate: 'Creation Date',
    requesttype: 'Request Type',
    eventlabel: 'Request Type',
    vin2: 'VIN2',
    reviewdate: 'Review Date',
    modstatuslabel: 'Status',
    reviewdatetowish: 'Review Date To Wish',
    finamount: 'Amount',
  };

  // Define globalColumnLabels for ExportXLS (for global columns)
  const globalColumnLabels: Record<string, string> = {
    tpreference: 'RRDI',
    tpname: 'Concession name',
    creationdate: 'Creation Date',
    requesttype: 'Request Type',
    eventlabel: 'Request Type',
    vin2: 'VIN2',
    reviewdate: 'Review Date',
    modstatuslabel: 'Status',
    reviewdatetowish: 'Review Date To Wish',
    finamount: 'Amount',
  };

  // Global filters and columns (when color !== "green")
  const globalFilterConfig: FilterConfig[] = [
    {
      key: 'tpreference',
      label: 'RRDI',
      type: 'text',
    },
    {
      key: 'tpname',
      label: 'Concession name',
      type: 'text',
    },
    ...filterConfig,
  ];

  const globalColumns = [
    {
      dataIndex: 'tpreference',
      key: 'tpreference',
    },
    {
      dataIndex: 'tpname',
      key: 'tpname',
    },
    ...columns,
  ];

  const globalColsXLS = [
    'tpreference',
    'tpname',
    'creationdate',
    'requesttype',
    'eventlabel',
    'vin2',
    'reviewdate',
    'modstatuslabel',
    'reviewdatetowish',
    'finamount',
  ];

  // Choose which config to use based on color
  const usedFilterConfig = color !== 'green' ? globalFilterConfig : filterConfig;
  const usedColumns = color !== 'green' ? globalColumns : columns;
  const usedColsXLS = color !== 'green' ? globalColsXLS : colsXLS;
  const usedColumnLabels = color !== 'green' ? globalColumnLabels : columnLabels;
  const handleFilterSearch = useCallback(
    (values: FilterValues) => {
      const formattedValues = {
        ...values,
        reviewdate: values.reviewdate
          ? values.reviewdate.format('YYYY-MM-DD')
          : '',
        reviewdatetowish: values.reviewdatetowish
          ? values.reviewdatetowish.format('YYYY-MM-DD')
          : '',
      };
      setSearchParams(formattedValues);
      refetch();
    },
    [refetch]
  );
  const handleFilterReset = useCallback(() => {
    filterForm.resetFields();
    setSearchParams({});
    refetch();
  }, [filterForm, refetch]);
  // Effect to handle tab activation
  useEffect(() => {
    console.log('color', color);
    console.log('usedColsXLS', usedColsXLS);
    console.log('usedColumnLabels', usedColumnLabels);
    if (isActive) {
      // Refetch data when tab becomes active
      refetch();
      onTabActivated?.();
    }
  }, [isActive, refetch, onTabActivated]);
  useEffect(() => {
    filterForm.setFieldsValue({ filteredData: modList });
  }, [filterForm]);

  return (
    <DisplayFramework.Provider pagcode="finmodifsfs" entity="CONTRACT">
      <Form form={form} className="bg-color-gray">
        <div className="bg-color-gray">
          <HeroTitle
            form={form}
            color={color || 'purple'}
            title="demands"
            showFilter
            display
            filterConfig={usedFilterConfig}
            onFilterSearch={handleFilterSearch}
            onFilterReset={handleFilterReset}
            className="productlist"
            noTitleOverlay
            itemsCompanyHierarchyTree={itemsCompanyHierarchyTree}
          />
        </div>

        <div className=" ut-title-page no-after">
          <Row justify="space-between" align="middle" className="mb-4 mt-6">
            <Col>
              <Button
                icon={<DeleteOutlined />}
                className={
                  selectedRows.length !== 0
                    ? 'no-border-radius  filter-item ant-btn-outline-primary bg-color-hover'
                    : 'no-border-radius  filter-item '
                }
                disabled={!isInspect}
                onClick={() => {
                  !isInspect && setmodalVisibleConfirmVisible(true);
                }}
              >
                <GetLabel label="deleteDemands" />
              </Button>
            </Col>
            <Col span={5} className="d-flex justify-end align-center">
              <ExportXLS
                data={modList || []}
                filename="demands"
                columns={usedColsXLS}
                columnLabels={usedColumnLabels}
              />
            </Col>
          </Row>
          <Row className="w-100 mb-1 d-flex align-center">
            <Col span={24}>
              <Form.Item name="filteredData" initialValue={modList}>
                <DataGrid
                  columns={usedColumns}
                  dataSource={modList}
                  size="large"
                  className="custom-data-grid"
                  rowKey="modid"
                  rowSelection={{
                    type: 'checkbox',
                    onChange: (selectedRowKeys, selectedRows) => {
                      setSelectedRows(selectedRows);
                    },
                    hideSelectAll: false,
                    columnWidth: 80,
                  }}
                  scrollInput={{ x: 1000, y: 550 }}
                />
              </Form.Item>
            </Col>
          </Row>
          <ConfirmModal
            className="modalConfirmOrCancel"
            visible={modalVisibleConfirm}
            status="error"
            message="Etes vous sure de vouloir supprimer ces demandes ?"
            onConfirm={() => {
              !isInspect && setmodalVisibleConfirmVisible(false);
            }}
            onCancel={() => {
              !isInspect && setmodalVisibleConfirmVisible(false);
            }}
            Confirmcomcode="valider"
            Cancelcomcode="close"
          />
        </div>
      </Form>
    </DisplayFramework.Provider>
  );
};

export default DemandSubTab;
